import { QueryInputType, Validation, queryInput } from '@/utils';
import {
  SortDirectionEnum,
  UserWithMarket,
  UserWithMarketConnection,
  UserWithMarketConnectionInput,
  UserWithMarketSearch,
} from '@/models/gen/graphql';

import { SearchUserWithMarketDocument } from '@/api/queries';
import graphApi from '@/api';

export const SEARCH_USER_WITH_MARKET_PAGE_SIZE = 100;
const SEARCHABLE_COLUMNS = ['username', 'firstName', 'lastName', 'email'];

export type SearchUserWithMarketFilters = {
  active?: number;
  roleId?: string;
  search?: string;
  airportCode?: string[];
  sorting?: { column: string; direction: SortDirectionEnum };
};

const [, runRefetchSearchUserWithMarket] = graphApi(SearchUserWithMarketDocument);

export type SearchUserWithMarketPage = {
  rows: UserWithMarket[];
  totalCount: number;
  endCursor: string;
  hasNextPage: boolean;
};

export const searchUserWithMarket = async (
  filters: SearchUserWithMarketFilters,
  pagination?: { after?: string }
): Promise<SearchUserWithMarketPage> => {
  const queryObj: UserWithMarketSearch = {
    active: Validation.isNumber(filters?.active) ? queryInput([filters.active]) : null,
    roleId: Validation.isValidUUID(filters?.roleId) ? queryInput(filters.roleId) : null,
    airportCodes:
      filters?.airportCode?.length > 0
        ? queryInput(
            [...filters.airportCode, '*'].map((code) => `%${code}%`),
            QueryInputType.LIKE
          )
        : null,
    [filters?.sorting?.column || 'createdAt']: queryInput([], QueryInputType.DEFAULT, filters.sorting.direction || SortDirectionEnum.Desc),
    id: queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Asc, 1),
  };

  const finalQueryObj =
    filters?.search?.trim().length > 0
      ? SEARCHABLE_COLUMNS.map((field): UserWithMarketSearch => {
          const likeSearchObj = {
            ...queryObj,
            [field]: queryInput([filters.search], QueryInputType.LIKE),
          };
          return likeSearchObj;
        })
      : [queryObj];

  const input: UserWithMarketConnectionInput = {
    first: SEARCH_USER_WITH_MARKET_PAGE_SIZE,
    after: pagination?.after ?? null,
    query: finalQueryObj,
  };

  const res = await runRefetchSearchUserWithMarket({ input });
  const connection = res?.searchUserWithMarket?.userWithMarketConnection as UserWithMarketConnection;
  return {
    rows: (connection?.edges || []).map((edge): UserWithMarket => edge.node as UserWithMarket),
    totalCount: connection?.totalCount || 0,
    endCursor: connection?.pageInfo?.endCursor,
    hasNextPage: connection?.pageInfo?.hasNextPage || false,
  };
};

export default searchUserWithMarket;
